"use client";

import React, { useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Search } from "lucide-react";
import serviceSearchIndex from "@/content/search/services-search-index.json";

type ServiceSearchItem = {
  id: string;
  label: string;
  href: string;
  description?: string;
  imageUrl?: string;
  imageAlt?: string;
  searchText?: string;
};

type ServiceMegaNavSearchProps = {
  className?: string;
  isMobile?: boolean;
  onNavigate?: () => void;
};

const searchItems = serviceSearchIndex.items as ServiceSearchItem[];

const normalize = (value: string) =>
  value.toLowerCase().replace(/\s+/g, " ").trim();

const getSearchText = (item: ServiceSearchItem) =>
  normalize(
    [item.label, item.description, item.href, item.searchText]
      .filter(Boolean)
      .join(" "),
  );

const preparedItems = searchItems.map((item) => ({
  ...item,
  normalizedSearchText: getSearchText(item),
}));

const styles = {
  root: {
    position: "relative",
  },
  icon: {
    position: "absolute",
    left: 12,
    top: "50%",
    transform: "translateY(-50%)",
    color: "#6b7280",
    pointerEvents: "none",
  },
  input: {
    width: "100%",
    height: 40,
    borderRadius: 9999,
    border: "1px solid #d1d5db",
    backgroundColor: "#ffffff",
    color: "#111827",
    padding: "0 14px 0 40px",
    fontSize: 13,
    lineHeight: "18px",
    outline: "none",
    boxSizing: "border-box",
  },
  mobileInput: {
    height: 44,
  },
  dropdown: {
    position: "absolute",
    left: 0,
    right: 0,
    top: "calc(100% + 8px)",
    zIndex: 80,
    overflow: "hidden",
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    backgroundColor: "#ffffff",
    boxShadow: "0 18px 40px rgba(15, 23, 42, 0.16)",
  },
  scroller: {
    maxHeight: 420,
    overflowY: "auto",
  },
  list: {
    listStyle: "none",
    margin: 0,
    padding: 0,
  },
  item: {
    margin: 0,
    padding: 0,
    borderTop: "1px solid #f3f4f6",
  },
  firstItem: {
    borderTop: "none",
  },
  link: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 14px",
    textDecoration: "none",
    backgroundColor: "#ffffff",
  },
  thumb: {
    width: 48,
    height: 48,
    flexShrink: 0,
    overflow: "hidden",
    borderRadius: 8,
    backgroundColor: "#f3f4f6",
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  content: {
    minWidth: 0,
    flex: 1,
  },
  label: {
    color: "#111827",
    fontSize: 13,
    lineHeight: "18px",
    fontWeight: 600,
    display: "block",
  },
  description: {
    color: "#6b7280",
    fontSize: 11,
    lineHeight: "15px",
    fontWeight: 400,
    display: "block",
    marginTop: 4,
  },
  empty: {
    padding: "10px 14px",
    fontSize: 13,
    lineHeight: "18px",
    color: "#6b7280",
  },
} satisfies Record<string, React.CSSProperties>;

const ServiceMegaNavSearch = ({
  className,
  isMobile = false,
  onNavigate,
}: ServiceMegaNavSearchProps) => {
  const [query, setQuery] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const trimmedQuery = normalize(query);
  const terms = trimmedQuery.split(" ").filter(Boolean);
  const minChars = 2;

  const results = useMemo(() => {
    if (trimmedQuery.length < minChars) return [];

    return preparedItems
      .filter((item) =>
        terms.every((term) => item.normalizedSearchText.includes(term)),
      )
      .slice(0, 8);
  }, [terms, trimmedQuery]);

  const shouldShowDropdown = isFocused && trimmedQuery.length >= minChars;

  return (
    <div className={className} style={styles.root}>
      <Search size={16} style={styles.icon} aria-hidden="true" />
      <input
        type="search"
        value={query}
        onChange={(event) => setQuery(event.currentTarget.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => window.setTimeout(() => setIsFocused(false), 120)}
        aria-label="Search services"
        placeholder="Search services"
        style={{
          ...styles.input,
          ...(isMobile ? styles.mobileInput : null),
        }}
      />
      {shouldShowDropdown && (
        <div style={styles.dropdown}>
          <div style={styles.scroller}>
            {results.length ? (
              <ul style={styles.list}>
                {results.map((item, index) => (
                  <li
                    key={item.id}
                    style={{
                      ...styles.item,
                      ...(index === 0 ? styles.firstItem : null),
                    }}
                  >
                    <Link
                      href={item.href}
                      onClick={() => {
                        setQuery("");
                        setIsFocused(false);
                        onNavigate?.();
                      }}
                      style={styles.link}
                    >
                      <span style={styles.thumb}>
                        {item.imageUrl && (
                          <Image
                            src={item.imageUrl}
                            alt={item.imageAlt ?? item.label}
                            width={48}
                            height={48}
                            style={styles.image}
                          />
                        )}
                      </span>
                      <span style={styles.content}>
                        <span style={styles.label}>{item.label}</span>
                        {!!item.description && (
                          <span style={styles.description}>
                            {item.description}
                          </span>
                        )}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <div style={styles.empty}>No matching services</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceMegaNavSearch;
